import React from 'react'

import { ModalActionTypes } from '@acorns/web-components'
import { FormikBag } from 'formik'

import MailingIcon from 'assets/icons/mailing-icon.svg'
import ResidentialIcon from 'assets/icons/residential-icon.svg'
import { IErrorDetails } from 'utils/Apollo'
import { FlowRoute } from 'utils/FlowProvider/types'

import {
  Suggestion,
  SuggestionBody,
  SuggestionHeader,
  SuggestionSeparator,
} from './styled-components'
import { AddressProps, AddressType, EnhancedSpendContactInfoProps } from './types'

export const createAddressErrorModal = (
  values: AddressProps,
  props: EnhancedSpendContactInfoProps,
  formikBag: FormikBag<EnhancedSpendContactInfoProps, AddressProps>,
): IErrorDetails => {
  const { addressType, closeErrorModal, saveAddress, setAddressType } = props
  const { setSubmitting } = formikBag

  return {
    image: addressType === AddressType.Mailing ? MailingIcon : ResidentialIcon,
    title: "We may not be able to verify this address if you don't edit it.",
    message: (
      <div>
        <div>{values.address1}</div>
        {values.address2 && <div>{values.address2}</div>}
        <div>
          {values.city}, {values.state} {values.zip}
        </div>
      </div>
    ),
    actions: [
      {
        type: ModalActionTypes.secondary,
        label: 'Continue',
        handler: () =>
          saveAddress(addressType, values)
            .then(() => {
              if (
                addressType === AddressType.Residential ||
                (addressType === AddressType.Mailing && values.alsoResidentialAddress)
              ) {
                closeErrorModal()
                props.route({ from: FlowRoute.ContactInfo, action: 'continue' })
                return
              } else {
                setSubmitting(false)
                setAddressType(AddressType.Residential)
                closeErrorModal()
              }
            })
            .catch(error => {
              // Error is already logged in saveAddress
              setSubmitting(false)
              closeErrorModal()
            }),
      },
      {
        type: ModalActionTypes.primary,
        label: 'Edit',
        handler: () => {
          setSubmitting(false)
          closeErrorModal()
        },
      },
    ],
  }
}

export const createAddressSuggestionModal = (
  values: AddressProps,
  props: EnhancedSpendContactInfoProps,
  formikBag: FormikBag<EnhancedSpendContactInfoProps, AddressProps>,
  suggestedAddress,
): IErrorDetails => {
  const { addressType, closeErrorModal, route, saveAddress, setAddressType } = props
  const { setSubmitting } = formikBag

  return {
    children: (
      <Suggestion>
        <SuggestionHeader>Something looks off</SuggestionHeader>
        <SuggestionBody>
          <div>{values.address1}</div>
          {values.address2 && <div>{values.address2}</div>}
          <div>
            {values.city}, {values.state} {values.zip}
          </div>
        </SuggestionBody>
        <SuggestionSeparator />
        <SuggestionHeader>Did you mean?</SuggestionHeader>
        <SuggestionBody>
          <div>{suggestedAddress.street1}</div>
          {suggestedAddress.street2 && <div>{suggestedAddress.street2}</div>}
          <div>
            {suggestedAddress.city}, {suggestedAddress.state} {suggestedAddress.zipCode}
          </div>
        </SuggestionBody>
      </Suggestion>
    ),
    actions: [
      {
        type: ModalActionTypes.secondary,
        label: 'Edit',
        handler: () => {
          setSubmitting(false)
          closeErrorModal()
        },
      },
      {
        type: ModalActionTypes.primary,
        label: 'Use suggested',
        handler: () => {
          // Translate suggested address into an address format that
          // can be persisted to the backend
          const addressValues: AddressProps = {
            address1: suggestedAddress.street1,
            address2: suggestedAddress.street2,
            city: suggestedAddress.city,
            state: suggestedAddress.state,
            zip: suggestedAddress.zipCode,
            alsoResidentialAddress: values.alsoResidentialAddress,
          }

          saveAddress(addressType, addressValues)
            .then(() => {
              if (
                addressType === AddressType.Residential ||
                (addressType === AddressType.Mailing && values.alsoResidentialAddress)
              ) {
                closeErrorModal()
                route({ from: FlowRoute.ContactInfo, action: 'continue' })
                return
              } else {
                setSubmitting(false)
                setAddressType(AddressType.Residential)
                closeErrorModal()
              }
            })
            .catch(error => {
              // Error is already logged in saveAddress
              setSubmitting(false)
              closeErrorModal()
            })
        },
      },
    ],
  }
}
